import { Divider, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { PersonaBubbleChartItem } from "../../../../types";
import {
  DASH_COLOR,
  FILL_COLOR_ACTIVE,
  hatchBackground,
} from "./personaGapChart.shared";

const GRID_TEMPLATE_COLUMNS = "1fr 96px 72px";
const GRID_COLUMN_GAP = "12px";

export const PersonaLegendBlock = ({
  personas,
}: {
  personas: PersonaBubbleChartItem[];
}) => {
  const theme = useTheme();
  const headerColor = theme.palette.custom.darkGrey;
  const expectedMarkerColor = theme.palette.primary.dark;

  const averageCoverage = personas.length
    ? Math.round(
        personas.reduce((acc, p) => acc + p.coverage, 0) / personas.length,
      )
    : 0;

  return (
    <Stack paddingTop={"10px"}>
      <Stack
        sx={{
          flexDirection: "row",
          gap: "12px",
          alignItems: "center",
          padding: "16px",
          border: "1px solid #DEDEDE",
          borderRadius: "8px",
        }}
      >
        <Typography variant={"body1"} sx={{ color: "#585858" }}>
          Average role coverage
        </Typography>
        <Divider orientation={"vertical"} flexItem={true} />
        <Stack
          sx={{
            flexDirection: "row",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <Typography
            variant={"subtitle1"}
            sx={{ color: "#232323", fontWeight: 500 }}
          >
            {averageCoverage}%
          </Typography>
          <Typography variant={"body1"} sx={{ color: "#232323" }}>
            across all roles
          </Typography>
        </Stack>
      </Stack>
      <Stack
        minWidth="386px"
        maxWidth="386px"
        gap="8px"
        padding="20px 8px"
        flexShrink={0}
      >
        <Stack
          sx={{
            padding: "0 8px",
            display: "grid",
            gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
            columnGap: GRID_COLUMN_GAP,
          }}
        >
          <Typography sx={{ color: headerColor }}>Persona</Typography>
          <Typography sx={{ color: headerColor, textAlign: "right" }}>
            Identified/Goal
          </Typography>
          <Typography sx={{ color: headerColor, textAlign: "right" }}>
            Coverage
          </Typography>
        </Stack>
        <Stack
          gap={"6px"}
          width={"100%"}
          sx={{ maxHeight: "280px", overflowY: "auto" }}
        >
          {personas.map((p) => (
            <Stack
              key={p.persona_id}
              sx={{
                padding: "4px 8px",
                display: "grid",
                gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
                columnGap: GRID_COLUMN_GAP,
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#121212",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
                title={p.persona_name}
              >
                {p.persona_name}
              </Typography>

              <Typography
                variant={"body1"}
                sx={{
                  color: "#121212",
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {p.actual}/{p.expected}
              </Typography>

              <Typography
                variant={"body1"}
                sx={{
                  color: "#121212",
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {p.coverage}%
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Stack gap={"8px"} paddingLeft={"8px"}>
          <Typography sx={{ color: headerColor, textAlign: "left" }}>
            Chart Indicators
          </Typography>
          <Stack flexDirection="row" alignItems="flex-start" gap="12px">
            <span
              style={{
                width: "12px",
                borderTop: `4px solid ${expectedMarkerColor}`,
                marginTop: "8px",
              }}
            />
            <Typography sx={{ color: "#121212" }}>
              Discovery Goal by Role
            </Typography>
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap="12px">
            <span
              style={{
                width: "12px",
                height: "12px",
                border: `1px solid ${FILL_COLOR_ACTIVE}`,
                backgroundImage: hatchBackground(FILL_COLOR_ACTIVE),
              }}
            />
            <Typography sx={{ color: "#121212" }}>
              Role Overrepresentation
            </Typography>
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap="12px">
            <span
              style={{
                width: "12px",
                height: "12px",
                border: `1px dashed ${DASH_COLOR}`,
              }}
            />
            <Typography sx={{ color: "#121212" }}>
              Role Underrepresentation
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
